import { defineStore, acceptHMRUpdate } from 'pinia'
import { 
  getAnnouncements, 
  createAnnouncement, 
  updateAnnouncement, 
  deleteAnnouncement 
} from '@/services/graphql/announcementService'
import {useLogger} from "@/composables/useLogger"
const {sendLog} = useLogger() 

/**
 * Announcement Store
 * 
 * Manages announcement data with caching layer to minimize API calls.
 * Follows defense-in-depth with validation and error handling.
 * 
 * Cache Strategy:
 * - Cache announcements globally
 * - Update cache on create/update/delete operations instead of invalidating
 * - Automatic cache expiration (default: 5 minutes)
 */

/**
 * Normalize announcement data to ensure consistent structure
 * Handles both old (_id, content) and new (id, text) field formats
 * @param {Object} announcement - Raw announcement object
 * @returns {Object} - Normalized announcement with id and text fields
 */
function normalizeAnnouncement(announcement) {
  if (!announcement) return null
  
  return {
    ...announcement,
    // Ensure 'id' field exists (use existing id or fallback to _id)
    id: announcement.id || announcement._id,
    // Ensure 'text' field exists (use existing text or fallback to content)
    text: announcement.text || announcement.content
  }
}

export const useAnnouncementStore = defineStore('announcement', {
  state: () => ({
    // Cache structure: { data: [], timestamp: Date.now() }
    announcementCache: null,
    
    // Loading states for different operations
    loadingStates: {
      fetch: false,
      create: false,
      update: false,
      delete: false
    },
    
    // Error tracking
    errors: {
      fetch: null,
      create: null,
      update: null,
      delete: null
    },
    
    // Cache expiration time (in milliseconds) - 5 minutes default
    cacheExpiration: 5 * 60 * 1000,
    
    // Track last successful operation for each type
    lastOperationTime: {
      fetch: null,
      create: null,
      update: null,
      delete: null
    }
  }),

  getters: {
    /**
     * Check if cache is valid
     * @param {Object} state - Pinia state
     * @returns {Boolean}
     */
    isCacheValid: (state) => {
      if (!state.announcementCache) return false
      
      const now = Date.now()
      const isExpired = (now - state.announcementCache.timestamp) > state.cacheExpiration
      
      return !isExpired
    },

    /**
     * Get cached announcements
     * @param {Object} state - Pinia state
     * @returns {Array|null}
     */
    getCachedAnnouncements: (state) => {
      if (!state.announcementCache) return null
      
      const now = Date.now()
      const isExpired = (now - state.announcementCache.timestamp) > state.cacheExpiration
      
      return isExpired ? null : state.announcementCache.data
    },

    /**
     * Check if any operation is currently loading
     * @param {Object} state - Pinia state
     * @returns {Boolean}
     */
    isAnyLoading: (state) => {
      return Object.values(state.loadingStates).some(loading => loading)
    },

    /**
     * Get all current errors
     * @param {Object} state - Pinia state
     * @returns {Object}
     */
    getAllErrors: (state) => {
      return state.errors
    }
  },

  actions: {
    /**
     * Fetch announcements
     * Uses cache if available and valid
     * 
     * @param {Object} params
     * @param {boolean} params.forceRefresh - Force bypass cache
     * @returns {Promise<Array>} - Array of announcements
     */
    async fetchAnnouncements({ forceRefresh = false } = {}) {
      // Check cache first unless force refresh
      if (!forceRefresh && this.isCacheValid) {
        const cached = this.getCachedAnnouncements
        if (cached !== null) {
          return cached
        }
      }

      this.loadingStates.fetch = true
      this.errors.fetch = null

      try {
        const response = await getAnnouncements()
        
        const announcements = response?.data?.announcements || []
        
        // Normalize all announcements to ensure consistent structure
        const normalizedAnnouncements = announcements.map(normalizeAnnouncement)
        
        // Update cache
        this.announcementCache = {
          data: normalizedAnnouncements,
          timestamp: Date.now()
        }
        
        this.lastOperationTime.fetch = Date.now()
        
        return normalizedAnnouncements
      } catch (error) {
        console.error('Error fetching announcements:', error)
        this.errors.fetch = error.message || 'Failed to fetch announcements'
        throw error
      } finally {
        this.loadingStates.fetch = false
      }
    },

    /**
     * Create a new announcement
     * Updates cache with new announcement instead of invalidating
     * 
     * @param {Object} announcementData
     * @param {string} announcementData.text - Announcement text
     * @returns {Promise<Object>} - Created announcement
     */
    async createNewAnnouncement(announcementData) {
      // Validation
      if (!announcementData.text || announcementData.text.trim()?.length === 0) {
        throw new Error('Announcement text cannot be empty')
      }

      this.loadingStates.create = true
      this.errors.create = null

      try {
        const response = await createAnnouncement(announcementData)
        
        const newAnnouncement = normalizeAnnouncement(response?.data?.announcement)
        
        // Update cache by adding new announcement to the beginning
        if (this.announcementCache) {
          sendLog("debug", "add_new_announcement", newAnnouncement)
          this.announcementCache.data.unshift(newAnnouncement)
          this.announcementCache.timestamp = Date.now()
        } else {
          // If no cache exists, create it with the new announcement
          this.announcementCache = {
            data: [newAnnouncement],
            timestamp: Date.now()
          }
        }
        
        this.lastOperationTime.create = Date.now()
        
        return newAnnouncement
      } catch (error) {
        console.error('Error creating announcement:', error)
        this.errors.create = error.message || 'Failed to create announcement'
        throw error
      } finally {
        this.loadingStates.create = false
      }
    },

    /**
     * Update an existing announcement
     * Updates cache with modified announcement
     * 
     * @param {Object} announcementData
     * @param {string} announcementData.id - Announcement ID
     * @param {string} announcementData.content - Updated announcement text
     * @returns {Promise<Object>} - Updated announcement
     */
    async updateExistingAnnouncement(announcementData) {
      // Validation
      if (!announcementData.id) {
        throw new Error('Announcement ID is required')
      }

      if (!announcementData.content || announcementData.content.trim()?.length === 0) {
        throw new Error('Announcement text cannot be empty')
      }

      this.loadingStates.update = true
      this.errors.update = null

      try {
        sendLog("debug", "update_new_announcement", announcementData)
        const response = await updateAnnouncement({
          id: announcementData.id,
          content: announcementData.content
        })
        
        const updatedAnnouncement = normalizeAnnouncement(response?.data?.announcement)
        
        // Update cache by finding and replacing the announcement
        if (this.announcementCache) {
          const index = this.announcementCache.data.findIndex(
            a => a.id === announcementData.id || a._id === announcementData.id
          )
          if (index !== -1) {
            this.announcementCache.data[index] = updatedAnnouncement
            this.announcementCache.timestamp = Date.now()
          }
        }
        
        this.lastOperationTime.update = Date.now()
        
        return updatedAnnouncement
      } catch (error) {
        console.error('Error updating announcement:', error)
        this.errors.update = error.message || 'Failed to update announcement'
        throw error
      } finally {
        this.loadingStates.update = false
      }
    },

    /**
     * Delete an announcement
     * Updates cache by removing deleted announcement
     * 
     * @param {Object} params
     * @param {string} params.id - Announcement ID
     * @returns {Promise<void>}
     */
    async deleteExistingAnnouncement({ id }) {
      // Validation
      if (!id) {
        throw new Error('Announcement ID is required')
      }

      this.loadingStates.delete = true
      this.errors.delete = null

      try {
        sendLog("debug", "delete_announcement", {id})
        await deleteAnnouncement(id)
        
        // Update cache by removing the deleted announcement
        if (this.announcementCache) {
          this.announcementCache.data = this.announcementCache.data.filter(
            a => a.id !== id && a._id !== id
          )
          this.announcementCache.timestamp = Date.now()
        }
        
        this.lastOperationTime.delete = Date.now()
      } catch (error) {
        console.error('Error deleting announcement:', error)
        this.errors.delete = error.message || 'Failed to delete announcement'
        throw error
      } finally {
        this.loadingStates.delete = false
      }
    },

    /**
     * Clear cached announcements
     * Useful for manual cache invalidation or user logout
     */
    clearCache() {
      sendLog("debug", "clear_announcement_cache")
      this.announcementCache = null
    },

    /**
     * Set cache expiration time
     * @param {number} milliseconds - Cache expiration time in milliseconds
     */
    setCacheExpiration(milliseconds) {
      this.cacheExpiration = milliseconds
    },

    /**
     * Clear all errors
     */
    clearErrors() {
      this.errors = {
        fetch: null,
        create: null,
        update: null,
        delete: null
      }
    },

    /**
     * Clear specific error
     * @param {string} operation - Operation type ('fetch', 'create', 'update', 'delete')
     */
    clearError(operation) {
      if (this.errors.hasOwnProperty(operation)) {
        this.errors[operation] = null
      }
    }
  }
})

// Enable Hot Module Replacement (HMR) for development
if (import.meta.webpackHot) {
  import.meta.webpackHot.accept(
    acceptHMRUpdate(useAnnouncementStore, import.meta.hot)
  )
}
